import { useEffect, useState } from 'react';

/**
 * Width bands in `rem`, mobile-first and half-open: compact < `tablet` ≤ tablet < `desktop` ≤
 * desktop. 37.5rem (600px at the default 16px font size) is Material's compact/medium edge;
 * 64rem is Tailwind 4's `lg`. A media query's `rem` follows the browser's default font size,
 * as Tailwind 4's breakpoints do, so the hook and the app's CSS switch together even when the
 * user has raised that size.
 */
const ResponsiveBreakpoints = {
  tablet: 37.5,
  desktop: 64,
} as const;

export interface DeviceTypeDetectionHook {
  isDesktop: boolean;
  isLoading: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isTouchScreen: boolean;
}

export const DeviceTypeDetectionVariant = {
  Default: 'default',
  DeviceOnly: 'device-only',
} as const;

export type DeviceTypeDetectionProps = {
  variant?: (typeof DeviceTypeDetectionVariant)[keyof typeof DeviceTypeDetectionVariant];
};

/* MDN's advice for UA sniffing: `Mobi` anywhere in the string, case as written. "Request
   Desktop Site" drops it (Android sends a Linux UA, iOS a Mac one), so the user's choice wins.
   Never OR this with `maxTouchPoints` or `pointer: coarse`: a phone keeps its touch screen in
   desktop mode, and that would put it back on the mobile layout.

   Not `navigator.userAgentData?.mobile`: Safari and Firefox don't ship it, so it would answer
   on Chromium only, and there it agrees with `Mobi` (Chrome's reduced UA keeps the token, and
   desktop mode flips both together). One rule for every browser is simpler to reason about. */
const MOBILE_USER_AGENT = /Mobi/;

const useMediaQuery = (query: string): boolean | undefined => {
  const [matches, setMatches] = useState<boolean>();

  useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener('change', update);
    return () => list.removeEventListener('change', update);
  }, [query]);

  return matches;
};

/**
 * Device detection, on two separate axes.
 *
 * - `isMobile` asks whether the browser wants a mobile experience: a `Mobi` user agent. With
 *   the default variant the window must also be compact (under 37.5rem), so a phone in
 *   landscape is not mobile; with `DeviceOnly` the user agent alone decides. Development and
 *   production use the same rule: test a phone with a Chrome DevTools device preset, not by
 *   narrowing a desktop window.
 * - The user agent and touch input are read once, on mount: a real device never changes
 *   them mid-session. After switching a DevTools device preset, reload. Widths follow the
 *   window live.
 * - `isTablet` / `isDesktop` are width bands from the same ladder CSS uses. Exactly one of
 *   compact (neither), tablet or desktop is true once loaded.
 *
 * @returns isMobile: a mobile user agent (and, by default, a compact window)
 * @returns isTablet: 37.5rem ≤ width < 64rem (600–1023px at the default font size), any user
 *   agent
 * @returns isDesktop: width ≥ 64rem (1024px at the default font size)
 * @returns isTouchScreen: the device has a touch input; true on touch laptops too, and not
 *   a stand-in for "mobile"
 * @returns isLoading: true until the first read, so a server render and the first client
 *   render agree
 */
export const useDeviceTypeDetection = (
  props?: DeviceTypeDetectionProps
): DeviceTypeDetectionHook => {
  const atLeastTablet = useMediaQuery(`(min-width: ${ResponsiveBreakpoints.tablet}rem)`);
  const atLeastDesktop = useMediaQuery(`(min-width: ${ResponsiveBreakpoints.desktop}rem)`);
  const [device, setDevice] = useState<{ isMobileAgent: boolean; isTouchScreen: boolean }>();

  useEffect(() => {
    setDevice({
      isMobileAgent: MOBILE_USER_AGENT.test(navigator.userAgent),
      isTouchScreen: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    });
  }, []);

  const isLoading = atLeastTablet === undefined || atLeastDesktop === undefined || !device;
  const isMobileAgent = device?.isMobileAgent ?? false;

  return {
    isMobile:
      props?.variant === DeviceTypeDetectionVariant.DeviceOnly
        ? isMobileAgent
        : isMobileAgent && atLeastTablet === false,
    isTablet: atLeastTablet === true && atLeastDesktop === false,
    isDesktop: atLeastDesktop === true,
    isTouchScreen: device?.isTouchScreen ?? false,
    isLoading,
  };
};
